import React, { useRef, useState } from 'react';

import DataAdminServiceFactory from '../../services/DataAdminServiceFactory';
import { SiteModel } from '../../models/DataAdminTypes';

export interface NewSubFolderModalProps {
  getAccessToken: () => Promise<string>;
  env: string;
  onClose: (folder: string | null) => void;
  site: SiteModel;
  caseFolder: string;
  currentSubFolder?: string;
}

export const NewSubFolderModal:
  React.FC<NewSubFolderModalProps> = ({ getAccessToken, env, onClose, site, caseFolder, currentSubFolder }) => {
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<{ subFolderName?: string; }>({});
    const [isCreated, setIsCreated] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [newSubFolderName, setNewSubFolderName] = useState<string | null>(null);

    const formRef = useRef<HTMLFormElement | null>(null);

    const dataAdminService = new DataAdminServiceFactory().create(env);

    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
      e.preventDefault();
      setSubmitting(true);
      try {
        if (!newSubFolderName?.trim()) {
          setFieldErrors({ subFolderName: 'Subfolder name is required' });
          setSubmitting(false);
          return;
        }
        const newPath = currentSubFolder ? currentSubFolder + '/' + newSubFolderName : newSubFolderName;
        const folderName = await dataAdminService.createSubFolder(
          await getAccessToken(), site.siteCode, caseFolder, newPath
        );
        setNewSubFolderName(folderName.replace(/\/$/, '')); // remove trailing slash if present
        setErrorMessage(null);
        setIsCreated(true);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        setErrorMessage(message || 'Failed to create subfolder');
        setSubmitting(false);
      }
    };

    const handleClose = (folder: string | null): void => {
      // Reset state on close
      setSubmitting(false);
      setErrorMessage(null);
      setFieldErrors({});
      setIsCreated(false);
      setNewSubFolderName(null);
      setIsFormValid(false);
      onClose(folder);
    };

    // Change form validaty based on field values
    React.useEffect(() => {
      if (newSubFolderName?.trim()) {
        setIsFormValid(true);
        setFieldErrors({});
      } else {
        setIsFormValid(false);
      }
    }, [newSubFolderName]);

    return (
      <div className="modal-background">
        <div className="modal-content w-150 h-8/10 max-w-8/10 max-h-9/10">

          <div className="modal-header">
            <h2 className="text-2xl font-semibold text-gray-900">
              Create Remote Subfolder
            </h2>

            <button
              className="modal-close-button"
              onClick={() => handleClose(null)}
              aria-label="Close modal"
            >
              <span className="text-2xl text-gray-500 hover:text-gray-700">×</span>
            </button>
          </div>
          <div>
            <b>Subfolder will be created under:</b><br /> {caseFolder}/{currentSubFolder}
          </div>

          {errorMessage && (
            <div className="bg-red-100 text-red-700 p-4 m-6 rounded">
              {errorMessage}
            </div>
          )}

          <div id="form-container" className="flex-1 flex items-center justify-center overflow-auto p-3">
            {isCreated ? (
              <div className="info-box-success w-full max-w-md flex flex-col items-center text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Subfolder created</h3>
                <div className="p-5"><b>Folder Name:</b> <br /> {newSubFolderName}</div>
                <button
                  onClick={() => handleClose(newSubFolderName)}
                  className="w-full max-w-md space-y-8"
                >
                  OK
                </button>
              </div>

            ) : (

              <form
                ref={formRef}
                onSubmit={handleSubmit}
                className="flex-1 overflow-y-auto p-6 space-y-8"
                noValidate
              >
                <div className="grid grid-cols-1 gap-6 w-75 max-w-md mx-auto justify-items-stretch">
                  {/* Subfolder Name */}
                  <div className="justify-center">
                    <label htmlFor="subFolderName" className="block text-sm font-medium text-gray-700">
                      Subfolder Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="subFolderName" name="subFolderName" type="text" autoComplete="off" required
                      value={newSubFolderName ?? ''}
                      onChange={e => setNewSubFolderName(e.target.value)}
                      className={
                        `${fieldErrors.subFolderName ?
                          'invalid' :
                          'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                        } `}
                      aria-describedby={fieldErrors.subFolderName ? 'caseId-error' : 'caseId-help'}
                    />
                    <p id="subFolderName-help" className="mt-1 text-xs text-gray-500">
                      A new subfolder to be created in the current location.
                    </p>
                    {fieldErrors.subFolderName && (
                      <p id="caseId-error" className="mt-1 text-xs text-red-600">
                        {fieldErrors.subFolderName}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
                  <button
                    form={formRef.current ? formRef.current.id : undefined}
                    type="submit"
                    onClick={() => {
                      // ensure form submit even if button is outside semantic form flow
                      formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                    }}
                    className={`${isFormValid ? 'action-primary' : 'action-disabled'} `}
                    disabled={submitting || !isFormValid}
                  >
                    {submitting ? 'Creating...' : 'Create'}
                  </button>
                </div>
              </form>
            )}
          </div>

        </div>

      </div>
    );
  };
